import { getCurrentInstance } from 'vue'
import type { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import { useCurrentUser } from '@/common/useCurrentUser'

export enum TelemetryEvent {
  JobExportAttempted = 'job_export_attempted',
  JobExportPaywallLearnMoreClicked = 'job_export_paywall_learn_more_clicked',
  JobFilterAutocompleteSuggestionClicked = 'job_filter_autocomplete_suggestion_clicked',
  ChangelogVisited = 'changelog_visited',
  JobAllResultsGroupSelected = 'job_all_results_group_selected',
}

export interface JobExportAttemptedTelemetryProperties {
  initiatedFrom: 'export_drawer'
  format: 'csv' | 'json' | 'pdf_zip'
  bucketSelection: string
  exportScope?: 'selected' | 'filters'
  dateFrom: string | undefined
  dateTo: string | undefined
  status: 'success' | 'failure'
  errorMessage?: string
}

export interface JobExportAttemptedTelemetryMeasurements {
  durationMs: number
  jobCount: number
  filterCount: number
  columnCount?: number
}

export interface JobExportPaywallLearnMoreClickedProperties {
  initiatedFrom: 'export_drawer'
}

export type JobExportPaywallLearnMoreClickedMeasurements = Record<string, never>

export interface JobFilterAutocompleteSuggestionClickedProperties {
  filterProperty: string
  displayValue: string
  searchText: string
}

export type JobFilterAutocompleteSuggestionClickedMeasurements = Record<string, never>

export type ChangelogVisitedTelemetryProperties = Record<string, never>

export interface ChangelogVisitedTelemetryMeasurements {
  durationMs: number
}

export type JobAllResultGroupSelectedProperties = Record<string, never>

export type JobAllResultsGroupSelectedMeasurements = Record<string, never>

export interface TelemetryEventDefinition<
  TProperties extends object,
  TMeasurements extends object,
> {
  properties: TProperties
  measurements: TMeasurements
}

export interface TelemetryEventMap {
  [TelemetryEvent.JobExportAttempted]: TelemetryEventDefinition<
    JobExportAttemptedTelemetryProperties,
    JobExportAttemptedTelemetryMeasurements
  >
  [TelemetryEvent.JobExportPaywallLearnMoreClicked]: TelemetryEventDefinition<
    JobExportPaywallLearnMoreClickedProperties,
    JobExportPaywallLearnMoreClickedMeasurements
  >
  [TelemetryEvent.JobFilterAutocompleteSuggestionClicked]: TelemetryEventDefinition<
    JobFilterAutocompleteSuggestionClickedProperties,
    JobFilterAutocompleteSuggestionClickedMeasurements
  >
  [TelemetryEvent.ChangelogVisited]: TelemetryEventDefinition<
    ChangelogVisitedTelemetryProperties,
    ChangelogVisitedTelemetryMeasurements
  >
  [TelemetryEvent.JobAllResultsGroupSelected]: TelemetryEventDefinition<
    JobAllResultGroupSelectedProperties,
    JobAllResultsGroupSelectedMeasurements
  >
}

export interface DefaultTelemetryProperties {
  userName: string
  userEmail: string
  userRole: string
  organizationName?: string
}

type TelemetryProperties<TEvent extends TelemetryEvent> = TelemetryEventMap[TEvent]['properties']

type TelemetryMeasurements<TEvent extends TelemetryEvent> =
  TelemetryEventMap[TEvent]['measurements']

export function useTelemetry() {
  const instance = getCurrentInstance()
  const appInsights = instance?.proxy?.$appInsights as ApplicationInsights | undefined
  const currentUser = useCurrentUser()
  const { currentOrg } = useCurrentOrg()

  /**
   * Tracks a custom Application Insights event.
   * Event-specific properties and measurements are defined by `TelemetryEventMap`.
   *
   * @param eventName Event name from `TelemetryEvent`.
   * @param properties Categorical fields used to filter or group events.
   * @param measurements Numeric values intended for aggregation.
   */
  function trackEvent<TEvent extends TelemetryEvent>(
    eventName: TEvent,
    properties: TelemetryProperties<TEvent>,
    measurements: TelemetryMeasurements<TEvent>,
  ) {
    appInsights?.trackEvent({
      name: eventName,
      properties: mergeProperties<DefaultTelemetryProperties, TelemetryProperties<TEvent>>(
        stripUndefined({
          userName: currentUser.value?.name ?? 'Global admin',
          userEmail: currentUser.value?.email,
          userRole: currentUser.value?.displayedRole,
          organizationName: currentOrg.value?.name,
        }),
        properties,
      ),
      measurements: mergeMeasurements(measurements),
    })
  }

  return {
    trackEvent,
  }
}

function stripUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as T
}

function mergeProperties<TLeft extends object, TRight extends object>(
  left: TLeft,
  right: TRight,
): TLeft & TRight {
  return stripUndefined({ ...left, ...right }) as TLeft & TRight
}

function mergeMeasurements<TMeasurements extends object>(
  measurements: TMeasurements,
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(measurements).filter(
      (entry): entry is [string, number] => entry[1] !== undefined,
    ),
  )
}
